import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PDPassword from "./PDPassword";
import PDRecoveryToken from "./PDRecoveryToken";
import PDOnboardingProfile from "../PDOnboardingProfile";
import PDOnboarding from "../PDOnboarding";
import IconTips from "./IconTips";
import PDUpdates from "./PDUpdates";
import PDJobGeneration from "../PDJobGeneration";
import PDGenZGrowthPath from "../PDGenZGrowthPath";
import PDContest from "../PDContest";
import PDSubscription from "../PDSubscription";
import PDDPIIT from "../PDDPIIT";
import PDFAQ from "../PDFAQ";
import SnapID from "../SnapID";
import PDTalkingCard from "../PDTalkingCard";
import IDGuide from "./IDGuide";
import CrashLanes from "../../iqgames/crashlanes/CrashLanes";
import AlienSwarm from "../../iqgames/alienswarm/AlienSwarm";
import DroneDashHome from "../../iqgames/dronedash/DroneDashHome";
import IQTesterHome from "../../iqgames/iqtester/IQTesterHome";
import TinyThink from "../../iqgames/tinythink/TinyThink";
import WonderEmojiHome from "../../iqgames/emojigrid/WonderEmojiHome";
import BestieGuide from "../../iqgames/BestieGuide";
import AICricketHome from "../../iqgames/aicricket/AICricketHome";
import GumballHistoryPage from "../../iqgames/components/GumballHistoryPage";
import FanRatingsHome from "../../iqgames/fanratings/FanRatingsHome";
import QuickSpinPage from "../../iqgames/quickspin/QuickSpinPage";

// SMALL REUSABLE PRIMITIVES
const DEFAULT_CLOSE_BTN =
  "absolute top-1 right-1 z-[9999] flex items-center justify-center h-6 w-6 rounded-full bg-black/70 text-white hover:bg-black shadow-lg";
const ROUNDED_CLOSE_BTN_LG =
  "absolute top-3 right-3 z-[9999] flex items-center justify-center h-10 w-10 rounded-full bg-black/70 text-white hover:bg-black shadow-lg";
const FIT_CLOSE_BTN =
  "absolute top-1 right-1 z-[9999] flex items-center justify-center w-fit px-2 py-1 rounded-lg bg-black/70 text-white hover:bg-black shadow-lg";

function MenuItem({ icon, label, onClick, danger = false, disabled = false }) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      className={`rounded-lg px-3 py-2 transition select-none ${
        disabled
          ? "opacity-40 cursor-not-allowed text-gray-400"
          : danger
            ? "text-red-500 hover:text-red-400 cursor-pointer"
            : "hover:bg-cyan-900/40 cursor-pointer"
      }`}
    >
      {icon} {label}
      {disabled && <span className="ml-2 text-xs">🔒</span>}
    </div>
  );
}

/** Generic overlay wrapper (was: ~20 near-identical bg-black/70 blocks) */
function OverlayModal({
  onClose,
  closeBtnClass = DEFAULT_CLOSE_BTN,
  wrapperClass = "",
  children,
}) {
  return (
    <div className="bg-black/70 flex items-center justify-center">
      <div className={`relative w-full ${wrapperClass}`}>
        <button onClick={onClose} className={closeBtnClass}>
          ✕
        </button>
        {children}
      </div>
    </div>
  );
}

function buildOverlayRegistry({
  t,
  userRole,
  currentTheme,
  userName,
  navigate,
}) {
  return {
    snapID: {
      render: () => <SnapID />,
    },
    pdProfile: {
      render: () => (
        <PDOnboardingProfile
          t={t}
          role={userRole}
          currentTheme={currentTheme}
        />
      ),
    },
    pdOnboarding: {
      render: () => (
        <PDOnboarding t={t} role={userRole} currentTheme={currentTheme} />
      ),
    },
    talkingCard: {
      render: () => <PDTalkingCard />,
    },
    changePassword: {
      render: () => (
        <PDPassword t={t} role={userRole} currentTheme={currentTheme} />
      ),
    },
    changeRecoveryToken: {
      render: () => (
        <PDRecoveryToken t={t} role={userRole} currentTheme={currentTheme} />
      ),
    },
    idGuide: {
      render: () => <IDGuide userName={userName} />,
      wrapperClass: "w-full",
      closeBtnClass: FIT_CLOSE_BTN,
      outerClass: "flex w-full",
    },
    crashLanes: {
      render: () => <CrashLanes userName={userName} />,
    },
    alienSwarm: {
      render: () => <AlienSwarm userName={userName} />,
      closeBtnClass: DEFAULT_CLOSE_BTN.replace("rounded-full", "rounded-lg"),
    },
    droneDash: {
      render: () => <DroneDashHome userName={userName} />,
      closeBtnClass: DEFAULT_CLOSE_BTN.replace("rounded-full", "rounded-lg"),
    },
    iqTester: {
      render: () => <IQTesterHome userName={userName} />,
      closeBtnClass: FIT_CLOSE_BTN,
      outerClass: "flex w-full",
    },
    tinyThink: {
      render: () => <TinyThink userName={userName} />,
      closeBtnClass: FIT_CLOSE_BTN,
      outerClass: "flex w-full",
    },
    emojiGrid: {
      render: () => <WonderEmojiHome userName={userName} />,
      closeBtnClass: FIT_CLOSE_BTN.replace("top-1 right-1", "top-0 right-0"),
      outerClass: "flex w-full",
    },
    gumballHistory: {
      render: () => <GumballHistoryPage />,
      closeBtnClass: FIT_CLOSE_BTN.replace("top-1 right-1", "top-0 right-0"),
      outerClass: "flex w-full",
    },
    fanRatings: {
      render: () => <FanRatingsHome userName={userName} />,
      closeBtnClass: FIT_CLOSE_BTN,
      outerClass: "flex w-full",
    },
    quickSpin: {
      render: () => <QuickSpinPage userName={userName} />,
      closeBtnClass: FIT_CLOSE_BTN,
      outerClass: "flex w-full",
    },
    aiCricket: {
      render: () => {
        navigate("/aicl");
        return null;
      },
      closeBtnClass: FIT_CLOSE_BTN.replace("top-1 right-1", "top-0 right-0"),
      outerClass: "flex w-full",
    },
    bestieGuide: {
      render: () => <BestieGuide userName={userName} />,
      closeBtnClass: FIT_CLOSE_BTN,
      outerClass: "flex w-full",
    },
    faq: {
      render: () => <PDFAQ />,
      wrapperClass: "border-y-2 border-cyan-200 rounded-2xl",
      closeBtnClass: DEFAULT_CLOSE_BTN.replace("rounded-full", "rounded-lg"),
    },
    dpiit: {
      render: () => <PDDPIIT />,
      wrapperClass: "border-y-2 border-cyan-200 rounded-2xl",
      closeBtnClass: DEFAULT_CLOSE_BTN.replace("rounded-full", "rounded-lg"),
    },
    genZJobs: {
      render: () => <PDJobGeneration />,
      wrapperClass: "border-y-2 border-cyan-200 rounded-2xl",
      closeBtnClass: DEFAULT_CLOSE_BTN.replace("rounded-full", "rounded-lg"),
    },
    genZGrowthPath: {
      render: () => <PDGenZGrowthPath />,
      wrapperClass: "border-y-2 border-cyan-200 rounded-2xl",
      closeBtnClass: DEFAULT_CLOSE_BTN.replace("rounded-full", "rounded-lg"),
    },
    iconTips: {
      render: () => <IconTips userName={userName} />,
      wrapperClass: "border-y-2 border-cyan-200 rounded-2xl",
      closeBtnClass: ROUNDED_CLOSE_BTN_LG,
    },
    updates: {
      render: () => <PDUpdates />,
      wrapperClass: "border-y-2 border-cyan-200 rounded-2xl",
      closeBtnClass: ROUNDED_CLOSE_BTN_LG,
    },
    contest: {
      render: () => <PDContest />,
      wrapperClass: "border-y-2 border-cyan-200 rounded-2xl",
    },
    gifting: {
      render: () => <PDSubscription />,
      wrapperClass: "border-y-2 border-cyan-200 rounded-2xl",
    },
    // prefMenu is special-cased below (has live inline form state) — see <PreferencesOverlay /> further down.
  };
}

const ID_MENU_ITEMS = [
  { key: "prefMenu", icon: "🎛️", label: "Preferences" },
  { key: "snapID", icon: "🆔", label: "SnapID" },
  { key: "pdProfile", icon: "👤", label: "PDProfile" },
  { key: "pdOnboarding", icon: "🧭", label: "Onboarding" },
  { key: "talkingCard", icon: "💬", label: "Talking ID Card" },
  { key: "changePassword", icon: "🔐", label: "Change Password" },
  { key: "changeRecoveryToken", icon: "🗝️", label: "Change Recovery Token" },
  { key: "idGuide", icon: "🪪", label: "ID Guide" },
];

const BESTIE_MENU_ITEMS = [
  { key: "crashLanes", icon: "🚘", label: "CrashLanes" },
  { key: "alienSwarm", icon: "👾", label: "AlienSwarm" },
  { key: "droneDash", icon: "𖥂", label: "DroneDash" },
  { key: "iqTester", icon: "🧠", label: "IQTester" },
  { key: "tinyThink", icon: "🐣", label: "TinyThink" },
  { key: "emojiGrid", icon: "🦋", label: "WonderEmoji" },
  { key: "gumballHistory", icon: "🍬", label: "GumballHistory" },
  { key: "fanRatings", icon: "⭐", label: "FanRatings" },
  { key: "quickSpin", icon: "🎡", label: "QuickSpin" },
  { key: "aiCricket", icon: "💜", label: "AICricketLeauge" },
  { key: "bestieGuide", icon: "🌟", label: "Bestie Guide" },
];

const ABOUT_MENU_ITEMS = [
  { key: "faq", icon: "❓", label: "FAQ" },
  { key: "dpiit", icon: "🏅", label: "DPIIT Recognition" },
  { key: "genZJobs", icon: "💼", label: "2026-27: 1L GenZ Job Mission" },
  { key: "genZUdaan", icon: "💼", label: "genZUdaan Program" },
  { key: "genZGrowthPath", icon: "📈", label: "GenZ Digital Growth Path" },
  { key: "updates", icon: "📢", label: "Platform Updates" },
  { key: "iconTips", icon: "👀", label: "Tips" },
];

// Items that should stay enabled even when moduleAllowed === false
const ALWAYS_ALLOWED_KEYS = new Set([
  "idGuide",
  "bestieGuide",
  // All ABOUT items
  "faq",
  "dpiit",
  "genZJobs",
  "genZUdaan",
  "genZGrowthPath",
  "updates",
  "iconTips",

]);

export function useOverlayState() {
  const [activeOverlay, setActiveOverlay] = useState(null); // string key or null
  const [showIDMenu, setShowIDMenu] = useState(false);
  const [showBestieMenu, setShowBestieMenu] = useState(false);
  const [showAboutMenu, setShowAboutMenu] = useState(false);
  const [showPrimeComponents, setShowPrimeComponents] = useState(true);

  const openOverlay = (key) => {
    setActiveOverlay(key);
    setShowIDMenu(false);
    setShowBestieMenu(false);
    setShowAboutMenu(false);
    setShowPrimeComponents(false);
  };

  const closeOverlay = () => {
    setActiveOverlay(null);
    setShowPrimeComponents(true);
  };

  return {
    activeOverlay,
    openOverlay,
    closeOverlay,
    showIDMenu,
    setShowIDMenu,
    showBestieMenu,
    setShowBestieMenu,
    showAboutMenu,
    setShowAboutMenu,
    showPrimeComponents,
    setShowPrimeComponents,
  };
}

export function PDHeaderAndOverlays({
  activeOverlay,
  openOverlay,
  closeOverlay,
  showIDMenu,
  setShowIDMenu,
  showBestieMenu,
  setShowBestieMenu,
  showAboutMenu,
  setShowAboutMenu,
  userName,
  t,
  userRole,
  currentTheme,
  handleLogout,
  selectedLanguage,
  setSelectedLanguage,
  handleRoleChange,
  ROLES,
  theme,
  setTheme,
  THEMES,
  languages,
  authState,
  moduleAllowed = false,
}) {
  const navigate = useNavigate();

  const registry = buildOverlayRegistry({
    t,
    userRole,
    currentTheme,
    userName,
    navigate,
  });

  const activeConfig = activeOverlay && registry[activeOverlay];

  // ───────────────────────────────────────────────
  // Access control helper
  // ───────────────────────────────────────────────
  const isItemEnabled = (key) => {
    if (moduleAllowed) return true;
    return ALWAYS_ALLOWED_KEYS.has(key);
  };

  return (
    <>
      <div className="flex items-center justify-between w-full mb-2">
        <div className="text-xs lg:text-sm px-2 w-full">
          {userName?.length > 0 ? (
            <div className="flex items-center justify-between w-full">
              {/* Left Section */}
              <div className="flex flex-row items-center justify-start gap-1">
                <div className="relative inline-block">
                  <span
                    onClick={() => {
                      setShowIDMenu((p) => !p);
                      setShowBestieMenu(false);
                      setShowAboutMenu(false);
                    }}
                    className="text-cyan-300 text-xs lg:text-sm font-semibold hover:text-cyan-200 transition-colors select-none underline underline-offset-2 decoration-dotted cursor-pointer"
                    title="Account menu"
                  >
                    {userName}
                  </span>
                  {showIDMenu && (
                    <div className="absolute top-8 left-1 right-0 z-50 bg-slate-900 border border-cyan-500 rounded-xl shadow-xl p-3 min-w-64">
                      {ID_MENU_ITEMS.map((item) => (
                        <MenuItem
                          key={item.key}
                          icon={item.icon}
                          label={item.label}
                          disabled={!isItemEnabled(item.key)}
                          onClick={() => {
                            openOverlay(item.key);
                            setShowIDMenu(false);
                          }}
                        />
                      ))}
                      <MenuItem
                        icon={<i className="fas fa-sign-out-alt mr-2" />}
                        label="Logout"
                        onClick={handleLogout}
                        danger
                      />
                    </div>
                  )}
                </div>

                <span>🔸</span>

                {/* Subscribe / Gift – restricted */}
                <div className="relative inline-block">
                  <span
                    onClick={() => {
                      if (!moduleAllowed) return;
                      setShowIDMenu(false);
                      setShowBestieMenu(false);
                      setShowAboutMenu(false);
                      openOverlay("gifting");
                    }}
                    className={
                      moduleAllowed
                        ? "cursor-pointer"
                        : "cursor-not-allowed opacity-40"
                    }
                    title={
                      moduleAllowed
                        ? "Subscriptions & Gifting"
                        : "Boarding Pass required"
                    }
                  >
                    <span className="inline-flex items-center whitespace-nowrap gap-0.5 rounded-md bg-gradient-to-r from-fuchsia-600 via-pink-500 to-orange-400 px-1 py-1 text-[10px] lg:text-sm font-semibold text-white shadow-lg shadow-pink-500/30 border border-pink-300/20">
                      <span className="lg:hidden">🎁</span>
                      <span className="hidden lg:inline">Subscribe / Gift</span>
                      {!moduleAllowed && (
                        <span className="ml-1 text-[9px]">🔒</span>
                      )}
                    </span>
                  </span>
                </div>

                <span>🔸</span>

                <div className="relative inline-block">
                  <span
                    onClick={() => {
                      setShowIDMenu(false);
                      setShowBestieMenu((p) => !p);
                      setShowAboutMenu(false);
                    }}
                    className="text-cyan-300 text-xs lg:text-sm font-semibold hover:text-cyan-200 transition-colors select-none underline underline-offset-2 decoration-dotted cursor-pointer"
                    title="Bestie Games & Fun"
                  >
                    🧸 Bestie
                  </span>
                  {showBestieMenu && (
                    <div className="absolute top-8 left-1 right-0 z-50 bg-slate-900 border border-cyan-500 rounded-xl shadow-xl p-3 min-w-48">
                      {BESTIE_MENU_ITEMS.map((item) => (
                        <MenuItem
                          key={item.key}
                          icon={item.icon}
                          label={item.label}
                          disabled={!isItemEnabled(item.key)}
                          onClick={() => {
                            openOverlay(item.key);
                            setShowBestieMenu(false);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <span>🔸</span>

                <div className="relative inline-block">
                  <span
                    onClick={() => {
                      setShowIDMenu(false);
                      setShowBestieMenu(false);
                      setShowAboutMenu((p) => !p);
                    }}
                    className="text-cyan-300 font-semibold underline underline-offset-2 decoration-dotted cursor-pointer hover:text-cyan-200 transition-colors"
                    title="About GenZ Job Mission"
                  >
                    <span className="text-xs lg:text-sm">Jobs & FAQ</span>
                  </span>
                  {showAboutMenu && (
                    <div className="absolute top-8 left-1/2 -translate-x-[55%] lg:left-1 lg:translate-x-0 z-50 bg-slate-900 border border-cyan-500 rounded-xl shadow-xl p-2 min-w-64">
                      {ABOUT_MENU_ITEMS.map((item) => (
                        <MenuItem
                          key={item.key}
                          icon={item.icon}
                          label={item.label}
                          disabled={!isItemEnabled(item.key)}
                          onClick={() => {
                            openOverlay(item.key);
                            setShowAboutMenu(false);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Section – Contest (restricted) */}
              <div className="flex items-center gap-1">
                <div className="relative inline-block">
                  <span
                    onClick={() => {
                      setShowIDMenu(false);
                      setShowBestieMenu(false);
                      setShowAboutMenu(false);
                      openOverlay("contest");
                    }}
                    className="cursor-pointer"
                    title="Think5Whys Gamified Contest"
                  >
                    <span className="inline-flex items-center whitespace-nowrap gap-0.5 rounded-md bg-gradient-to-r from-fuchsia-600 via-pink-500 to-orange-400 px-1 py-1 text-[10px] lg:text-sm font-semibold text-white shadow-lg shadow-pink-500/30 border border-pink-300/20">
                      <span className="lg:hidden">🚗🏁🏆</span>
                      <span className="hidden lg:inline">
                        🚗 Gamified Contest
                      </span>
                    </span>
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <span className="text-gray-300 text-xs lg:text-sm">Guest</span>
          )}
        </div>
      </div>

      {activeOverlay === "prefMenu" ? (
        <PreferencesOverlay
          onClose={closeOverlay}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          userRole={userRole}
          handleRoleChange={handleRoleChange}
          ROLES={ROLES}
          theme={theme}
          setTheme={setTheme}
          THEMES={THEMES}
          languages={languages}
          authState={authState}
        />
      ) : (
        activeConfig && (
          <OverlayModal
            onClose={closeOverlay}
            wrapperClass={activeConfig.wrapperClass}
            closeBtnClass={activeConfig.closeBtnClass}
          >
            {activeConfig.render()}
          </OverlayModal>
        )
      )}
    </>
  );
}

function PreferencesOverlay({
  onClose,
  selectedLanguage,
  setSelectedLanguage,
  userRole,
  handleRoleChange,
  ROLES,
  theme,
  setTheme,
  THEMES,
  languages,
  authState,
}) {
  return (
    <div className="bg-black/70 flex items-center justify-center p-2">
      <div className="relative w-full lg:max-w-2xl border-y-2 border-cyan-200 rounded-2xl bg-gray-900/80 p-4 space-y-5">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-[9999] flex items-center justify-center h-7 w-7 rounded-full bg-black/70 text-white hover:bg-black shadow-lg"
        >
          ✕
        </button>

        <div className="flex flex-col gap-2">
          <p className="text-[10px] lg:text-xs text-cyan-300 italic">
            🌍 Choose the language that makes your learning journey feel
            natural.
          </p>
          <label className="text-[10px] lg:text-xs font-semibold uppercase tracking-wide text-cyan-200">
            Language
          </label>
          <select
            value={authState?.user?.language || "en"}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className="bg-gray-800 border border-gray-600 text-white rounded-xl px-3 py-2 text-[10px] lg:text-xs h-10 focus:outline-none focus:border-cyan-400"
          >
            {languages.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-[10px] lg:text-xs text-cyan-300 italic">
            🚀 Select your role to personalize your experience and
            opportunities.
          </p>
          <label className="text-[10px] lg:text-xs font-semibold uppercase tracking-wide text-cyan-200">
            Your Role
          </label>
          <select
            value={userRole}
            onChange={(e) => handleRoleChange(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2 text-[10px] lg:text-xs h-10 focus:outline-none focus:border-cyan-400"
          >
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {role.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-[10px] lg:text-xs text-cyan-300 italic">
            🎨 Pick a theme that matches your mood and makes every session
            enjoyable.
          </p>
          <label className="text-[10px] lg:text-xs font-semibold uppercase tracking-wide text-cyan-200">
            App Theme
          </label>
          <select
            value={authState?.user?.theme || "DARK"}
            onChange={(e) => setTheme(e.target.value)}
            className="bg-gray-800 border border-gray-600 text-white rounded-xl px-3 py-2 text-[10px] lg:text-xs h-10 focus:outline-none focus:border-cyan-400"
          >
            {Object.entries(THEMES).map(([key, value]) => (
              <option key={key} value={key}>
                {value.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
